import { AsyncPipe } from '@angular/common';
import { Component, effect, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { map, Subject } from 'rxjs';
import { SynlabHeaderService } from '../../../../../../../synlab-access-ui-components/synlab-header/synlab-header.service';
import { ProfileNavItem, RightSidebarComponent } from "../right-sidebar/right-sidebar.component";
import { NavbarService } from './navbar.service';
import { RightSidebarMenuComponent } from '../right-sidebar-menu/right-sidebar-menu.component';
import { RouterModule } from '@angular/router';
import { LayoutService } from '../layout-service/layout.service';
import { Claim } from '../../../../../../../synlab-access-ui-components/core';
import { UserTypes } from './../../../../../../../enums/user-type.enum';
import { hasAnyUserType } from './../../../../../../../utils/user-type-claims';
import { PageTitle } from './../../../../../../../enums/page-title.enum';

/**
 * User types that must not be able to navigate away via the navbar logo.
 *
 * This is the single place to change that policy: add a `UserTypes` entry here
 * and the logo is rendered as a non-interactive image for those users. Neither
 * the template nor `isLogoDisabled` needs to be touched.
 */
export const USER_TYPES_WITH_DISABLED_LOGO: readonly UserTypes[] = [
  UserTypes.RESULT_VIEWER,
];

@Component({
    selector: 'app-navbar',
    imports: [AsyncPipe, TranslocoModule, RightSidebarComponent, RightSidebarMenuComponent, RouterModule],
    providers: [SynlabHeaderService],
    templateUrl: './navbar.component.html',
    styleUrl: './navbar.component.scss'
})
export class NavbarComponent implements OnInit, OnDestroy {

  /**
   * Accessible name for the logo link.
   *
   * The app name rather than "Home": several routes render a Home entry in the menu as well,
   * so labelling the logo that way left two adjacent links reading identically. A brand name
   * needs no translation, and this is the same constant the document title uses.
   */
  public readonly appName = PageTitle.SYNLAB;

  private readonly layoutService = inject(LayoutService);

  private readonly navbarService = inject(NavbarService);

  @Input()
  public userName: string = '';

  @Input()
  public currentLangCode: string = '';

  @Input()
  public logoUrl: string = '';

  @Output()
  public logOut = new Subject<void>();

  @Input()
  public currentUserClaims: Claim[] = [];

  @Input()
  public logoRedirectUrl: string = '';

  public readonly links$ = this.headerService.links$;

  /**
   * True while at least one link is actually rendered by the menu.
   *
   * A link whose `urlGenerator` resolves to an empty string / `false` and that
   * has no `route` is filtered out by the template, so counting entries is not
   * enough — the burger menu would open an empty sidebar (as it does for a
   * `result_viewer`, whose only other entry, Home, is hidden).
   */
  public readonly hasMenuLinks$ = this.headerService.links$.pipe(
    map(links => (links ?? []).some(link => !!link.url || !!link.route)),
  );

  public readonly showRightSidebar = this.layoutService.showRightSidebar;

  public readonly sidebarView = this.layoutService.sidebar;

  public readonly isNavbarHidden = this.navbarService.isHidden;

  private readonly onDestroy$ = new Subject<void>();

  @Input()
  public selectedMpiId: string = '';

  @Input()
  public readonly wards: any[] = [];

  /** Forwarded to {@link RightSidebarComponent} - see its doc comment (SA-21731). */
  @Input()
  public profileNavItems: ProfileNavItem[] = [];

  /**
   * The element that opened the right sidebar (`.user-info` or `.mobile-burger-menu`),
   * so closing it - via Escape, its own close button, or the overlay behind it - hands
   * keyboard focus back rather than stranding it on `<body>` once `[attr.inert]` takes
   * the sidebar out of the accessibility tree.
   */
  private lastRightSidebarTrigger: HTMLElement | null = null;

  constructor(private readonly headerService: SynlabHeaderService) {
    effect(() => {
      if (this.showRightSidebar()) {
        this.lastRightSidebarTrigger = document.activeElement as HTMLElement;
      } else if (this.lastRightSidebarTrigger) {
        this.lastRightSidebarTrigger.focus();
        this.lastRightSidebarTrigger = null;
      }
    });
  }

  /**
   * True when the user holds any user type listed in
   * {@link USER_TYPES_WITH_DISABLED_LOGO}.
   */
  public get isLogoDisabled(): boolean {
    return hasAnyUserType(this.currentUserClaims, USER_TYPES_WITH_DISABLED_LOGO);
  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
  }

  /**
   * Query params for a routed link: the navbar's own `langCode` plus whatever
   * the link declared for itself. The link wins on a clash, since it knows why
   * it set the param.
   */
  public routeQueryParams(link: { queryParams?: Record<string, string> }): Record<string, string> {
    return { langCode: this.currentLangCode, ...(link.queryParams ?? {}) };
  }

  public formatUrlWithLangCode(url: string | null) {
    if (!url) return url;

    const addQueryParam = (url: string, param: string, value: string) => {
      try {
        const urlObj = new URL(url);
        if (!urlObj.searchParams.has(param) && value) {
            urlObj.searchParams.append(param, value);
        }
        return urlObj.toString();
      } catch (error) {
        return url;
      }
    }

    return addQueryParam(url, 'langCode', this.currentLangCode);
  }
}
